import React, { CSSProperties } from 'react';
import { Row, Col, Image } from 'antd';
import { useQuery } from '@apollo/client';
import { queries } from '../../../graphql/graphql';
import { IPlan } from '../../../types/plans';
import { Loader } from '../../UI/Loader';
import { blockTitle } from './styles';
import { titleStyle } from '../../UI/Title';

interface MapResult {
  snapshot: string;
  map: {
    address: string;
  };
}

interface PlanMapViewProps {
  mapId?: string;
  plan: IPlan;
}

const styles: {
  mapSnapshot: CSSProperties;
  mapInformation: CSSProperties;
  mapInformationSVG: CSSProperties;
  view3dContainer: CSSProperties;
  view3d: CSSProperties;
  imageView3d: CSSProperties;
} = {
  mapSnapshot: {
    backgroundColor: 'rgb(245, 245, 245)',
    aspectRatio: '1 / 1',
    height: '100%',
    width: '100%',
    objectFit: 'cover',
  },
  mapInformation: {
    backgroundColor: 'rgb(245, 245, 245)',
    padding: '1.5em',
  },
  mapInformationSVG: {
    backgroundColor: 'rgb(245, 245, 245)',
    height: '100%',
    width: '100%',
    aspectRatio: '1 / 1',
    objectFit: 'contain',
  },
  view3dContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  view3d: {
    position: 'absolute',
    backgroundColor: '#EE8865',
    width: '450px',
    borderRadius: '5px',
    height: '67.5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '20px',
    lineHeight: '36px',
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 150,
    color: '#000000',
    letterSpacing: '0.55px',
    opacity: 0.85,
    transition: 'opacity 1s',
  },
  imageView3d: {
    width: '100%',
    height: 'calc((75vw + 100dvh)/3)',
    objectFit: 'cover',
    filter: 'blur(10px)',
  },
} as const;

export const PlanMapView: React.FC<PlanMapViewProps> = ({ mapId, plan }) => {
  const { data, loading } = useQuery<{ mapResult: MapResult }>(queries.map.MAP_RESULT, {
    variables: { id: mapId },
    skip: !mapId,
  });

  if (!mapId || (!loading && !data?.mapResult)) {
    const handleClick = () => {
      try {
        localStorage.setItem('lastViewed', plan.id!);
      } catch (error) {
        console.error(error);
      }
    };

    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <span style={blockTitle}>Map</span>
            </Col>
          </Row>
          <Row>
            <Col span={24} lg={12}>
              <a
                style={styles.view3dContainer}
                href={'/'}
                onClick={handleClick}
                target="_blank"
                rel="noreferrer">
                <img
                  src={'/public/images/map_placeholder.png'}
                  alt="Configure"
                  style={styles.imageView3d}
                />
                <div style={{ ...styles.view3d, width: '300px' }}>
                  Overlay this floor plan over your property
                </div>
              </a>
            </Col>
            <Col span={24} lg={12} style={styles.mapInformation}>
              <Col span={24}>
                <Image
                  src={`https://3d.homewip.com/svg/${plan.planId}/`}
                  style={styles.mapInformationSVG}
                />
              </Col>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const { snapshot, map } = data?.mapResult || {
    snapshot: '',
    map: { address: '' },
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Row>
          <Col span={24}>
            <span style={blockTitle}>Map</span>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={styles.mapInformation}>
            <span style={titleStyle}>Address: {map.address || 'Address is empty'}</span>
          </Col>
          <Col span={24} lg={12} style={styles.mapSnapshot}>
            <img src={snapshot} alt="Configure" style={styles.mapSnapshot} />
          </Col>
          <Col span={24} lg={12} style={styles.mapInformation}>
            <Col span={24}>
              <Image
                src={`https://3d.homewip.com/svg/${plan.planId}/`}
                style={styles.mapInformationSVG}
              />
            </Col>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
